import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';

import { createRootStore, type RootStore } from './root.store';

/**
 * React Context for the MobX root store. Mirrors the mobile pattern
 * (`packages/mobile/src/stores/StoreProvider.tsx`): a single composition
 * root is created at the top of the tree and exposed via `useStore()`.
 *
 * Tests can pass a pre-built `store` to `<StoreProvider store={...}>`
 * to isolate behaviour; production code lets `StoreProvider` build the
 * default one.
 */
const StoreContext = createContext<RootStore | null>(null);

type StoreProviderProps = {
  children: ReactNode;
  /** Override the default RootStore — used by tests to inject fixtures. */
  store?: RootStore | undefined;
};

export const StoreProvider = ({ children, store }: StoreProviderProps): ReactElement => {
  const value = useMemo(() => store ?? createRootStore(), [store]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = (): RootStore => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStore() called outside <StoreProvider>. Wrap the tree at App.tsx.');
  }
  return store;
};

export const useAuthStore = (): RootStore['auth'] => useStore().auth;
